import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDataProvider, useRefresh, useNotify } from "react-admin";
import { sendSentryException } from "../api/SentryErrorHandler";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { permissionCheck, renderIfPermissions } from "../api/permissionManager";
import SyncIcon from "@mui/icons-material/Sync";

const appBrandingConfigs = {
  login_logo: {
    title: "1. Login Logo",
    subtitle: "This image is displayed at the top of your mobile app’s login screen.",
    upload_guidelines: [
      "Aspect ratio: 2:1 (wide rectangle)",
      "Recommended size: 1000 × 500 px",
      "Minimum: 400 × 200 px",
      "Maximum: 2000 × 1000 px",
      "Format: PNG",
    ],
    dimensions: { width: 1000, height: 500 },
  },
  dashboard_logo: {
    title: "2. Dashboard Logo",
    subtitle: "This image is displayed on the driver dashboard screen.",
    upload_guidelines: [
      "Aspect ratio: 2:1 (wide rectangle)",
      "Recommended size: 600 × 300 px",
      "Minimum: 200 × 100 px",
      "Maximum: 1200 × 600 px",
      "Format: PNG",
      "Upload both Light Mode and Dark Mode versions for best results",
    ],
    dimensions: { width: 600, height: 300 },
  },
  app_icon: {
    title: "3. App Icon",
    subtitle: "This is the image shown on the device home screen and in app switchers.",
    upload_guidelines: [
      "1024×1024 px, PNG, sRGB",
      "Max file size: 1 MB",
      "No rounded corners or shadows",
      "Keep important content centered (Google Play applies rounding)",
      "Design should remain clear at smaller sizes",
    ],
    dimensions: { width: 1024, height: 1024 },
  }
};

const getIndicatorType = (mediaType) => {
  if (mediaType === "android_app_icon") return "android";
  if (mediaType === "ios_app_icon") return "ios";
  return (mediaType.includes("dark")) ? "dark" : "light";
};

/*
 * Visual components
 */
export const MediaTypeDropdown = ({ primaryMediaType, secondaryMediaType = null, autoExpanded = false, ...props }) => {
  const mediaType = primaryMediaType === "android_app_icon" ? "app_icon" : primaryMediaType;
  const config = appBrandingConfigs[mediaType];
  if (!config) return null;

  const clientKey = props.id;
  return (
    <Accordion
      data-testid={`${mediaType}_dropdown`}
      defaultExpanded={autoExpanded}
      sx={{
        width: "100%",
        border: "1px solid #e0e0e0",
        borderRadius: "8px !important",
        mb: 2,
        boxShadow: "none",
        bgcolor: "#FAFAFA",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ alignItems: "flex-start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{config.title}</Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "0.875rem",
              color: "text.secondary",
            }}
          >
            {config.subtitle}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} justifyContent={"center"}>
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "center" }}>
            {renderIfPermissions(
              props.permissions,
              "clients",
              "read_only",
              <AppBrandingImage
                clientKey={clientKey}
                mediaType={primaryMediaType}
                currentInfo={props[primaryMediaType]}
                permissions={props.permissions}
                dimensions={config.dimensions}
              />
            )}
          </Grid>

          {secondaryMediaType && (
            <Grid item xs={6} sx={{ display: "flex", justifyContent: "center" }}>
              {renderIfPermissions(
                props.permissions,
                "clients",
                "read_only",
                <AppBrandingImage
                  clientKey={clientKey}
                  mediaType={secondaryMediaType}
                  currentInfo={props[secondaryMediaType]}
                  permissions={props.permissions}
                  dimensions={config.dimensions}
                />
              )}
            </Grid>
          )}
        </Grid>
        <Box sx={{ width: "100%", mt: 4, mb: 1 }}>
          <Guidelines mediaType={primaryMediaType} />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export const AppBrandingImage = ({ clientKey, mediaType, dimensions, currentInfo, permissions }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  // values we get from the API
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [originalFilename, setOriginalFileName] = useState(null);

  // states
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [imageHandle, setImageHandle] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // file and img handling
  const fileRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imageObj, setImageObj] = useState(null);

  // call GET on assets/<handle> to get all necessary information that /app_branding doesn't provide
  useEffect(() => {
    if (currentInfo && currentInfo.handle) {
      setUploadStatus("fetching");
      dataProvider
        .getOne(`assets`, { id: clientKey, handle: currentInfo.handle })
        .then(({ data }) => {
          setThumbnailUrl(data.thumbnail_url);
          setDownloadUrl(data.media_url);
          setOriginalFileName(data.original_filename);
          setCreatedAt(formatDate(data.created_at));
        })
        .finally(() => setUploadStatus("idle"));
    } else {
      setThumbnailUrl(null);
      setDownloadUrl(null);
      setUploadStatus("idle");
    }
  }, [currentInfo, clientKey, dataProvider]);

  const handleSuccess = useCallback(() => {
    setImageHandle(null);
    setUploadStatus("idle");
    if (fileRef.current) {
      setOriginalFileName(fileRef.current.name);
    }
    refresh();
    notify("Upload successful", { type: "success" });
  }, [refresh, notify]);

  const handleFailure = useCallback(
    (errorMsg) => {
      setImageHandle(null);
      setUploadStatus("idle");
      notify(errorMsg, { type: "error" });
    },
    [notify]
  );

  // continuously poll GET /assets after upload to see if FilePreviews processing has finished
  // has timeout
  useProcessingStatusPolling(imageHandle, clientKey, mediaType, handleSuccess, handleFailure);

  const handleFileUpload = (event) => {
    const uploadedFile = event.dataTransfer ? event.dataTransfer.files[0] : event.target.files[0];
    if (!uploadedFile) return;

    fileRef.current = uploadedFile;
    if (uploadedFile.type !== "image/png") {
      alert("Invalid file type! Please upload a png.");
      return;
    }
    setUploadStatus("idle");

    const img = new Image();
    const objectUrl = URL.createObjectURL(uploadedFile);
    img.onload = () => {
      setImageObj(img);
      // this prompts the resizing dialog to open
      setUploadStatus("resizing");
      URL.revokeObjectURL(objectUrl);
    };
    img.src = objectUrl;
    return;
  };

  const upload = async (file) => {
    // android needs a notification icon which is a monochrome version of the app icon
    if (mediaType === "android_app_icon") {
      const notificationIconFile = await generateNotificationIcon(file);
      const notificationIcon = await uploadAsset("notification_icon", notificationIconFile);
      const androidAppIcon = await uploadAsset(mediaType, file);

      setImageHandle({
        android_app_icon: androidAppIcon.handle,
        notification_icon: notificationIcon.handle,
      });
    } else {
      const asset = await uploadAsset(mediaType, file);
      setImageHandle(asset.handle);
    }
  };

  const uploadAsset = async (mediaType, file) => {
    // POST /assets
    const { data: uploadInfo, error } = await dataProvider.create("assets", {
      id: clientKey,
      data: {
        media_type: mediaType,
        original_filename: file.name,
      },
    });

    // upload to S3
    const s3_response = await fetch(uploadInfo.upload_destination, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!s3_response.ok) {
      const s3_upload_error = new Error(
        `Error when uploading app asset to S3 for ${clientKey}. Status: ${s3_response.status}`
      );
      throwSentryError(s3_upload_error, "S3", "PUT", s3_response.body);
      throw s3_upload_error;
    }

    // PUT /assets
    const { data: assetInfo, put_error } = await dataProvider.update("assets", {
      id: clientKey,
      code: uploadInfo.handle,
    });
    return assetInfo;
  };

  const handleSaveAfterResizing = async (blob) => {
    const finalFile = new File([blob], fileRef.current.name || "original.png", {
      type: "image/png",
    });
    fileRef.current = finalFile;

    setUploadStatus("uploading");

    try {
      await upload(finalFile);
      setUploadStatus("polling");
      refresh();
    } catch (error) {
      setUploadStatus("idle");
      notify(`App asset failed to upload. Check Sentry.`, { type: "error" });
      sendSentryException(error, "handleSaveAfterResizing");
    }
  };

  const handleCancelResizing = () => {
    setUploadStatus("idle");
    fileRef.current = null;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRetry = async () => {
    if (!downloadUrl) return;

    try {
      setUploadStatus("fetching");

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Failed to fetch original image");
      const blob = await response.blob();

      const file = new File([blob], originalFilename || "original.png", {
        type: "image/png",
      });
      fileRef.current = file;

      await upload(file);
      setUploadStatus("polling");
      refresh();
    } catch (error) {
      setUploadStatus("idle");
      notify("Retry failed, check Sentry", { type: "error" });
      sendSentryException(error, "handleRetry");
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e);
  };

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        flexBasis: "480px",
        flexGrow: 0.8,
        fontFamily: "Roboto, sans-serif",
      }}
    >
      {uploadStatus === "fetching" ? (
        <div
          style={{
            border: "1px dashed #8c929d",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "240px",
          }}
        >
          <CircularProgress size={32} />
        </div>
      ) : thumbnailUrl && !isDragging ? (
        <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
          <AppBrandingCard
            thumbnailUrl={thumbnailUrl}
            mediaType={mediaType}
            fileName={originalFilename}
            handle={currentInfo.handle}
            timestamp={createdAt || "N/A"}
            onReplace={() => fileInputRef.current.click()}
            permissions={permissions}
          />
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {renderIfPermissions(
              permissions,
              "clients",
              "write",
              <SupportToolbar mediaType={mediaType} handleRetry={handleRetry} downloadUrl={downloadUrl} />
            )}
          </div>
        </div>
      ) : permissionCheck("clients", "write", permissions) ? (
        <UploadField
          mediaType={mediaType}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current.click()}
          isDragging={isDragging}
          hasThumbnail={!!thumbnailUrl}
        />
      ) : permissionCheck("clients", "read_only", permissions) ? (
        <ReadOnlyEmptyAsset mediaType={mediaType} />
      ) : null}

      <input
        data-testid={`${mediaType}_upload`}
        type="file"
        name="file"
        accept="image/png"
        ref={fileInputRef}
        onChange={(e) => {
          handleFileUpload(e);
          e.target.value = null;
        }}
        hidden
      />

      <ImageResizeDialog
        open={uploadStatus === "resizing"}
        onClose={handleCancelResizing}
        imageObj={imageObj}
        exportWidth={dimensions.width}
        exportHeight={dimensions.height}
        onSave={handleSaveAfterResizing}
        mediaType={mediaType}
      />
      <ProcessingDialog open={uploadStatus === "uploading" || uploadStatus === "polling"} />
    </div>
  );
};;

export const ReadOnlyEmptyAsset = ({ mediaType }) => {
  return (
    <div>
      <div
        style={{
          border: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          borderRadius: "8px",
          padding: "36px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div data-testid={`${mediaType}_not_set`} style={{ fontSize: "15px", color: "#8c929d", fontStyle: "italic" }}>
          app branding asset not set
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginTop: "6px",
          color: "#3c4043",
        }}
      >
        <AssetIndicator mediaType={mediaType} />
      </div>
    </div>
  );
};

export const UploadField = ({ mediaType, onDragOver, onDragLeave, onDrop, onClick, isDragging }) => {
  return (
    <>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onClick}
        style={{
          border: `1px dashed ${isDragging ? "#4eb5f1" : "#8c929d"}`,
          backgroundColor: isDragging ? "#f0f8ff" : "transparent",
          borderRadius: "8px",
          padding: "36px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          textAlign: "center",
          minHeight: "240px",
        }}
      >
        <div
          data-testid={`${mediaType}_no_thumbnail`}
          style={{ fontSize: "15px", color: "#212121", lineHeight: "1.5" }}
        >
          <span style={{ color: "#2196f3", cursor: "pointer" }}>Click</span> or drag file to
          <br />
          this area to upload
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginTop: "6px",
          color: "#3c4043",
          fontSize: "1.1rem",
        }}
      >
        <AssetIndicator mediaType={mediaType} />
      </div>
    </>
  );
};

export const AssetIndicator = ({ mediaType }) => {
  const type = getIndicatorType(mediaType)
  if (type === "android")
    return (
      <>
        <AndroidIndicator />
        <span>Android</span>
      </>
    );
  if (type === "ios")
    return (
      <>
        <IOSIndicator />
        <span>iOS</span>
      </>
    );
  if (type === "dark")
    return (
      <>
        <DarkModeIndicator />
        <span>Dark Mode</span>
      </>
    );
  return (
    <>
      <LightModeIndicator />
      <span>Light Mode</span>
    </>
  );
};

export const LightModeIndicator = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

export const DarkModeIndicator = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

export const AndroidIndicator = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.22-.28.15-.4.54-.23.85l1.85 3.19C2.42 11.53 0 16.03 0 21h24c0-4.97-2.42-9.47-6.4-11.52zM8.5 17.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm7 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
  </svg>
);

export const IOSIndicator = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 13.56c-.02-2.31 1.89-3.42 1.98-3.47-1.07-1.56-2.74-1.78-3.34-1.81-1.42-.14-2.77.84-3.51.84-.73 0-1.85-.82-3.03-.8-1.53.02-2.95.89-3.74 2.27-1.6 2.76-.41 6.84 1.15 9.09.76 1.1 1.66 2.33 2.87 2.29 1.16-.05 1.62-.75 3.03-.75 1.41 0 1.84.75 3.05.73 1.25-.02 2.02-1.12 2.78-2.23.88-1.28 1.24-2.52 1.26-2.58-.02-.01-2.47-.95-2.48-3.58zM14.61 5.36c.64-.78 1.07-1.86.95-2.94-1.04.04-2.16.63-2.82 1.4-.59.68-1.1 1.8-.95 2.85 1.16.09 2.18-.53 2.82-1.31z" />
  </svg>
);

export const DownloadButton = ({ mediaType, downloadUrl }) => {
  return (
    <Tooltip title="Download Original">
      <IconButton
        data-testid={`${mediaType}_downloadButton`}
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        size="small"
        sx={{
          padding: "6px",
          backgroundColor: "#f0f8ff",
          "&:hover": { backgroundColor: "#e3f2fd" },
        }}
      >
        <DownloadIcon color="primary" fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export const SupportToolbar = ({ mediaType, handleRetry, downloadUrl }) => {
  return (
    <div
      style={{
        border: "1px solid #000000",
        borderRadius: "8px",
        padding: "16px",
        width: "100%",
        backgroundColor: "#337AB7",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {downloadUrl && <DownloadButton mediaType={mediaType} downloadUrl={downloadUrl} />}
        {downloadUrl && <RetryButton mediaType={mediaType} handleRetry={handleRetry} />}
      </div>
    </div>
  );
};

export const RetryButton = ({ mediaType, handleRetry }) => {
  return (
    <button
      data-testid={`${mediaType}_retryUpload`}
      onClick={handleRetry}
      style={{
        backgroundColor: "#c94949",
        color: "white",
        border: "none",
        borderRadius: "4px",
        padding: "6px 14px",
        fontSize: "13px",
        fontWeight: 600,
        textTransform: "uppercase",
        cursor: "pointer",
        opacity: 1,
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
        transition: "background-color 0.2s",
        height: "34px",
      }}
      onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#b71c1c")}
      onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#d32f2f")}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M23 4v6h-6" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
      Retry Upload
    </button>
  );
};

export const ImageResizeDialog = ({ open, onClose, imageObj, exportWidth, exportHeight, onSave, mediaType }) => {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isCanvasDragging, setIsCanvasDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [canvasElement, setCanvasElement] = useState(null);

  const PADDING = 100;
  const VISUAL_WIDTH = exportWidth + PADDING * 2;
  const VISUAL_HEIGHT = exportHeight + PADDING * 2;

  useEffect(() => {
    if (open) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [open, imageObj]);

  useEffect(() => {
    if (!imageObj || !canvasElement || !open) return;

    const ctx = canvasElement.getContext("2d");
    ctx.clearRect(0, 0, VISUAL_WIDTH, VISUAL_HEIGHT);

    ctx.save();
    ctx.translate(VISUAL_WIDTH / 2 + position.x, VISUAL_HEIGHT / 2 + position.y);
    ctx.scale(zoom, zoom);
    ctx.drawImage(imageObj, -imageObj.width / 2, -imageObj.height / 2);
    ctx.restore();

    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
    ctx.beginPath();
    ctx.rect(0, 0, VISUAL_WIDTH, VISUAL_HEIGHT);
    ctx.rect(PADDING, PADDING, exportWidth, exportHeight);
    ctx.fill("evenodd");

    ctx.strokeStyle = "#1976d2";
    ctx.lineWidth = 2;
    ctx.strokeRect(PADDING, PADDING, exportWidth, exportHeight);
  }, [imageObj, zoom, position, canvasElement, open, VISUAL_WIDTH, VISUAL_HEIGHT, exportWidth, exportHeight]);

  const handleCanvasMouseDown = (e) => {
    setIsCanvasDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasMouseMove = (e) => {
    if (!isCanvasDragging || !canvasElement) return;
    const rect = canvasElement.getBoundingClientRect();
    const scaleX = VISUAL_WIDTH / rect.width;
    const scaleY = VISUAL_HEIGHT / rect.height;
    const deltaX = (e.clientX - lastMousePos.x) * scaleX;
    const deltaY = (e.clientY - lastMousePos.y) * scaleY;
    setPosition((prev) => ({ x: prev.x + deltaX, y: prev.y + deltaY }));
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleCanvasMouseUp = () => setIsCanvasDragging(false);

  const handleModalSave = () => {
    if (!imageObj) return;

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = exportWidth;
    exportCanvas.height = exportHeight;
    const exportCtx = exportCanvas.getContext("2d");

    // iOS cannot have a transparent background, so we turn it white
    if (mediaType === "ios_app_icon") {
      exportCtx.fillStyle = "#FFFFFF";
      exportCtx.fillRect(0, 0, exportWidth, exportHeight);
    }

    exportCtx.translate(exportWidth / 2 + position.x, exportHeight / 2 + position.y);
    exportCtx.scale(zoom, zoom);
    exportCtx.drawImage(imageObj, -imageObj.width / 2, -imageObj.height / 2);

    exportCanvas.toBlob((blob) => {
      if (blob) onSave(blob);
    }, "image/png");
  };

  return (
    <Dialog data-testid={`resizer_dialog`} open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Position & size image</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
          p: 3,
        }}
      >
        <canvas
          data-testid={`resizing_canvas`}
          ref={setCanvasElement}
          width={VISUAL_WIDTH}
          height={VISUAL_HEIGHT}
          style={{
            maxWidth: "100%",
            maxHeight: "50vh",
            aspectRatio: `${VISUAL_WIDTH} / ${VISUAL_HEIGHT}`,
            backgroundImage:
              "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
            backgroundSize: "20px 20px",
            backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
            backgroundColor: "#ffffff",
            border: "1px solid #1976d2",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            cursor: isCanvasDragging ? "grabbing" : "grab",
            display: "block",
          }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
        />
      </DialogContent>
      <Box px={3} py={2} display="flex" alignItems="center" gap={2}>
        <Typography>Zoom</Typography>
        <Slider
          data-testid={`zoom_slider`}
          value={zoom}
          min={0.1}
          max={3}
          step={0.05}
          onChange={(e, newZoom) => setZoom(newZoom)}
        />
      </Box>
      <DialogActions sx={{ padding: 2 }}>
        <Button data-testid={`cancel_resizing`} onClick={onClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button data-testid={`save_resizing`} onClick={handleModalSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ProcessingDialog = ({ open }) => {
  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Processing your image...</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 5,
        }}
      >
        <CircularProgress size={48} sx={{ mb: 3 }} />
        <Typography variant="body1" align="center" color="textPrimary">
          Please wait while we verify and save your branding image.
        </Typography>
        <Typography variant="body2" align="center" color="textSecondary" sx={{ mt: 1 }}>
          This usually takes just a few seconds.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export const Guidelines = ({ mediaType }) => {
  const config = mediaType === "android_app_icon" ? appBrandingConfigs["app_icon"] : appBrandingConfigs[mediaType];
  if (!config?.upload_guidelines) return null;
  return (
    <Box
      data-testid={`${mediaType}_guidelines`}
      sx={{
        bgcolor: "#e1e5ea",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem", mb: 1.5 }}>Guidelines:</Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        {config.upload_guidelines?.map((item, i) => (
          <Box component="li" key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
            <Box
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                bgcolor: "text.primary",
                mt: "7px",
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "0.875rem" }}>{item}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const AppBrandingCard = ({
  thumbnailUrl,
  mediaType,
  fileName = "original.png",
  handle,
  timestamp,
  onReplace,
  permissions,
}) => {
  return (
    <div
      style={{
        border: "1px solid #337AB7",
        borderRadius: "8px",
        padding: "16px",
        width: "100%",
        backgroundColor: "#ffffff",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          border: "1px solid #d3d3d3",
          borderRadius: "6px",
          padding: "2px",
          backgroundImage: `
            linear-gradient(45deg, #e0e0e0 25%, transparent 25%), 
            linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
            linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
          `,
          backgroundSize: "10px 10px",
          backgroundPosition: "0 0, 0 5px, 5px 5px, 5px 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "120px",
        }}
      >
        <img
          src={thumbnailUrl}
          data-testid={`${mediaType}_thumbnail`}
          style={{ maxWidth: "100%", height: "auto", display: "block" }}
        />
      </div>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
        style={{
          color: "#1a73e8",
          textDecoration: "none",
          fontSize: "1.25rem",
          margin: "16px 0 0 0",
          wordBreak: "break-all",
        }}
      >
        <Typography data-testid={`${mediaType}_filename`}>{fileName}</Typography>
        <Typography data-testid={`${mediaType}_handle`} sx={{ color: "text.secondary" }}>
          Handle: {handle}
        </Typography>
      </Box>

      {renderIfPermissions(
        permissions,
        "clients",
        "write",
        <div
          data-testid={`${mediaType}_replace`}
          onClick={onReplace}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "16px",
            color: "#3c4043",
            cursor: "pointer",
          }}
        >
          <SyncIcon fontSize="medium" />
          <span style={{ fontSize: "1.25rem" }}>Replace</span>
        </div>
      )}

      {timestamp && timestamp !== "N/A" && (
        <div
          data-testid={`${mediaType}_timestamp`}
          style={{
            marginTop: "16px",
            color: "#5f6368",
            fontSize: "1rem",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          <span>last updated at:</span>
          <span>{timestamp}</span>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginTop: "16px",
          color: "#3c4043",
          fontSize: "1.1rem",
        }}
      >
        <AssetIndicator mediaType={mediaType} />
      </div>
    </div>
  );
};

/*
 * Helper functions for logic
 */

const useProcessingStatusPolling = (imageHandle, clientKey, mediaType, onSuccess, onFailure) => {
  const dataProvider = useDataProvider();

  useEffect(() => {
    if (!imageHandle) return;

    let timer;
    let retryCount = 0;
    const maxRetries = 7;

    const checkStatus = async () => {
      try {
        const status = await getProcessingStatus(dataProvider, clientKey, mediaType, imageHandle);

        if (status === "success") {
          await patchAppBranding(dataProvider, clientKey, mediaType, imageHandle);
          onSuccess();
        } else if (status === "failure") {
          onFailure("Upload failed - thumbnail processing failed. Please try again.");
        } else {
          if (++retryCount >= maxRetries) {
            onFailure("Timed out waiting for thumbnail. Please try again.");
            return;
          }
          timer = setTimeout(checkStatus, 3000);
        }
      } catch (err) {
        onFailure("Network error during polling");
      }
    };

    checkStatus();
    return () => clearTimeout(timer);
  }, [imageHandle, clientKey, mediaType, onSuccess, onFailure]);
};

const getProcessingStatus = async (dataProvider, clientKey, mediaType, imageHandle) => {
  if (mediaType === "android_app_icon") {
    const { data: notificationIconData } = await dataProvider.getOne("assets", {
      id: clientKey,
      handle: imageHandle.notification_icon,
    });
    const { data: appIconData } = await dataProvider.getOne("assets", {
      id: clientKey,
      handle: imageHandle.android_app_icon,
    });
    if (notificationIconData.status === appIconData.status) {
      return appIconData.status;
    } else if (notificationIconData.status === "failure" || appIconData.status === "failure") {
      return "failure";
    } else {
      return appIconData.status;
    }
  } else {
    const { data } = await dataProvider.getOne("assets", {
      id: clientKey,
      handle: imageHandle,
    });
    return data.status;
  }
};

const patchAppBranding = async (dataProvider, clientKey, mediaType, imageHandle) => {
  let request_body;
  if (mediaType === "android_app_icon") {
    request_body = {
      android_app_icon: imageHandle.android_app_icon,
      notification_icon: imageHandle.notification_icon,
    };
  } else {
    request_body = { [mediaType]: imageHandle };
  }
  await dataProvider.update("app_branding", {
    id: clientKey,
    body: request_body,
  });
};

const generateNotificationIcon = (appIconFile) => {
  /*
   * Modified script provided by Nick
   * Convert an image to a monochrome silhouette matching the old ImageMagick script.
   * Flattens onto white, thresholds at 70%, and makes the light areas transparent.
   */
  return new Promise((resolve, reject) => {
    const lumincanceThreshold = 0.7;
    const colorPixels = 255;
    // Threshold value of what pixels get turned black
    const threshold = lumincanceThreshold * colorPixels;

    const img = new Image();

    const objectUrl = URL.createObjectURL(appIconFile);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      // Draw the original image onto the canvas
      ctx.drawImage(img, 0, 0);

      // Extract the raw pixel data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Loop through every pixel simulate monotone function from android resizer script
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // 1. 'image.alpha "remove"')
        const alphaNorm = a / 255;
        const flatR = r * alphaNorm + 255 * (1 - alphaNorm);
        const flatG = g * alphaNorm + 255 * (1 - alphaNorm);
        const flatB = b * alphaNorm + 255 * (1 - alphaNorm);

        // 2. Calculate luminance of the flattened pixel
        const luminance = flatR * 0.299 + flatG * 0.587 + flatB * 0.114;

        // 3. 'threshold "70%"' and 'transparent "white"')
        if (luminance > threshold) {
          // Brighter than 70% -> becomes white -> becomes transparent
          data[i + 3] = 0; // Set alpha to 0
        } else {
          // Darker than 70% -> becomes the solid icon shape
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
          data[i + 3] = 255;
        }
      }

      // Put the modified pixels back onto the canvas
      ctx.putImageData(imageData, 0, 0);

      // 3. Export as a Blob, then wrap in a File object
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas conversion to Blob failed."));
          return;
        }

        // Construct a new filename based on the original
        const originalName = appIconFile.name || "original.png";
        const newFileName = `notification_${originalName}`;

        const newFile = new File([blob], newFileName, {
          type: "image/png",
        });

        resolve(newFile);
      }, "image/png");
    };

    img.onerror = () => {
      // Clean up even if it fails
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for processing"));
    };

    img.src = objectUrl;
  });
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${year}/${month}/${day} ${hours12}:${minutes}${ampm} UTC+00:00`;
};
